import React, { useState, useCallback } from 'react';
import {
  Typography,
  Input,
  Card,
  Alert,
  Spin,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Divider,
  Avatar,
} from 'antd';
import {
  SearchOutlined,
  PhoneOutlined,
  UserOutlined,
  IdcardOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useClientSearch } from '../../features/clients/hooks';
import { ClientInfo } from '../../features/clients/types';

const { Title, Text, Paragraph } = Typography;

const PHONE_PREFIX = '8-800-070-';

export function SearchPage() {
  const { searchByPhone, isLoading, error, result, clearResult } = useClientSearch();
  const [phoneSuffix, setPhoneSuffix] = useState('');

  const formatSuffix = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    return digits;
  };

  const handleSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSuffix(e.target.value);
    setPhoneSuffix(formatted);
    if (!formatted) {
      clearResult();
    }
  };

  const handleSearch = useCallback(async () => {
    if (phoneSuffix.length !== 4) {
      return;
    }
    const fullPhone = `8800070${phoneSuffix}`;
    await searchByPhone(fullPhone);
  }, [searchByPhone, phoneSuffix]);

  const formatPhoneDisplay = (phone: string) => {
    if (phone.length === 11) {
      return `${phone[0]}-${phone.slice(1, 4)}-${phone.slice(4, 7)}-${phone.slice(7, 9)}-${phone.slice(9)}`;
    }
    return phone;
  };

  const getStatusConfig = (client: ClientInfo) => {
    // For postpaid clients, don't show active/blocked status
    if (client.clientType === 'postpaid') {
      return {
        color: '#8c8c8c',
        bgColor: '#fafafa',
        borderColor: '#d9d9d9',
        icon: <CreditCardOutlined />,
        text: 'Постоплата',
        hideStatus: true,
      };
    }
    // For prepaid clients, show active/blocked status
    if (client.isBlocked) {
      return {
        color: '#ff4d4f',
        bgColor: '#fff2f0',
        borderColor: '#ffccc7',
        icon: <StopOutlined />,
        text: 'Заблокирован',
        hideStatus: false,
      };
    }
    if (!client.isEnabled) {
      return {
        color: '#faad14',
        bgColor: '#fffbe6',
        borderColor: '#ffe58f',
        icon: <ExclamationCircleOutlined />,
        text: 'Отключён',
        hideStatus: false,
      };
    }
    return {
      color: '#52c41a',
      bgColor: '#f6ffed',
      borderColor: '#b7eb8f',
      icon: <CheckCircleOutlined />,
      text: 'Активен',
      hideStatus: false,
    };
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header Section */}
      <div style={{ marginBottom: 28 }}>
        <Title level={3} style={{ marginBottom: 6 }}>
          <SearchOutlined style={{ marginRight: 10, color: '#1890ff' }} />
          Поиск клиента
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 13, margin: 0 }}>
          Поиск prepaid-клиентов по номеру 8-800. Введите последние 4 цифры для поиска.
        </Paragraph>
      </div>

      {/* Search Section */}
      <Card
        style={{
          marginBottom: 28,
          borderRadius: 10,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 14 }}>
            НОМЕР ТЕЛЕФОНА
          </Text>

          <Space.Compact size="large">
            <Input
              value={PHONE_PREFIX}
              disabled
              style={{
                width: 130,
                textAlign: 'center',
                backgroundColor: '#fafafa',
                color: '#262626',
                fontWeight: 600,
                fontSize: 16,
                height: 42,
                borderColor: '#d9d9d9',
              }}
            />
            <Input
              placeholder="0000"
              value={phoneSuffix}
              onChange={handleSuffixChange}
              onPressEnter={handleSearch}
              maxLength={4}
              style={{
                width: 90,
                textAlign: 'center',
                fontSize: 16,
                fontWeight: 600,
                height: 42,
                letterSpacing: 4,
              }}
              autoFocus
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={isLoading}
              onClick={handleSearch}
              disabled={phoneSuffix.length !== 4}
              style={{
                height: 42,
                paddingLeft: 20,
                paddingRight: 20,
                fontSize: 14,
              }}
            >
              Поиск
            </Button>
          </Space.Compact>

          {phoneSuffix.length > 0 && phoneSuffix.length < 4 && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Введите ещё {4 - phoneSuffix.length} цифр{4 - phoneSuffix.length === 1 ? 'у' : (4 - phoneSuffix.length < 5 ? 'ы' : '')}
              </Text>
            </div>
          )}
        </div>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card style={{ borderRadius: 12, textAlign: 'center', padding: 36 }}>
          <Spin size="default" />
          <div style={{ marginTop: 14 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>Поиск клиента...</Text>
          </div>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Alert
          message={<span style={{ fontSize: 14, fontWeight: 600 }}>Клиент не найден</span>}
          description={
            <span style={{ fontSize: 12 }}>
              Prepaid-клиент с номером <strong>{PHONE_PREFIX}{phoneSuffix}</strong> не найден.
              <br />
              Проверьте номер и попробуйте снова.
            </span>
          }
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined style={{ fontSize: 22 }} />}
          style={{
            borderRadius: 12,
            padding: '18px 22px',
          }}
        />
      )}

      {/* Result Card */}
      {result && !isLoading && (
        <Card
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Status Banner */}
          {(() => {
            const status = getStatusConfig(result);
            return (
              <div
                style={{
                  backgroundColor: status.bgColor,
                  borderBottom: `1px solid ${status.borderColor}`,
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Space>
                  <span style={{ color: status.color, fontSize: 16 }}>{status.icon}</span>
                  <Text strong style={{ color: status.color, fontSize: 14 }}>
                    {status.text}
                  </Text>
                </Space>
                <Tag color={result.clientType === 'prepaid' ? 'blue' : 'default'} style={{ margin: 0, fontSize: 11, padding: '2px 8px' }}>
                  {result.clientType === 'prepaid' ? 'PREPAID' : 'POSTPAID'}
                </Tag>
              </div>
            );
          })()}

          {/* Main Content */}
          <div style={{ padding: 24 }}>
            <Row gutter={[20, 20]} align="middle">
              {/* Avatar & Name Section */}
              <Col xs={24}>
                <Space size={16} align="start">
                  <Avatar
                    size={60}
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: '#1890ff',
                      fontSize: 26,
                    }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0, marginBottom: 6 }}>
                      {result.name}
                    </Title>
                    <Space size={6}>
                      <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                      <Text style={{ fontSize: 16, fontWeight: 500 }}>
                        {formatPhoneDisplay(result.phone)}
                      </Text>
                    </Space>
                  </div>
                </Space>
              </Col>
            </Row>

            <Divider style={{ margin: '20px 0' }} />

            {/* Info Grid */}
            <Row gutter={[28, 16]}>
              <Col xs={12} sm={8}>
                <div>
                  <Space size={6} style={{ marginBottom: 6 }}>
                    <IdcardOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Аккаунт
                    </Text>
                  </Space>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>{result.account}</Text>
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={8}>
                <div>
                  <Space size={6} style={{ marginBottom: 6 }}>
                    <CreditCardOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Тариф
                    </Text>
                  </Space>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>
                      {result.plan?.name || 'N/A'}
                    </Text>
                  </div>
                </div>
              </Col>

              {result.contragent && (
                <Col xs={24} sm={8}>
                  <div>
                    <Space size={6} style={{ marginBottom: 6 }}>
                      <UserOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Контрагент
                      </Text>
                    </Space>
                    <div>
                      <Text strong style={{ fontSize: 14 }}>
                        {result.contragent.name}
                      </Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        </Card>
      )}

      {/* Empty State - Initial */}
      {!result && !error && !isLoading && phoneSuffix.length === 0 && (
        <Card
          style={{
            borderRadius: 12,
            textAlign: 'center',
            backgroundColor: '#fafafa',
            border: '2px dashed #d9d9d9',
          }}
          bodyStyle={{ padding: 48 }}
        >
          <PhoneOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
          <Title level={5} type="secondary" style={{ marginBottom: 10 }}>
            Введите номер телефона
          </Title>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
            Введите последние 4 цифры номера 8-800-070-XXXX для поиска
          </Paragraph>
        </Card>
      )}
    </div>
  );
}
